import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
// Components
import NavigationHeader, { PaymentType } from "./NavigationHeader";
import OrderGrid from "./PaymentGrid";
import EinvoicesPage from "./EinvoicesPage";

// Utils & Store
import { clearToken } from "../../utils/auth";
import { RootDispatch, RootState } from "../../store";

// Models
import { IOrderValidation } from "../../models/Order.model";
import {
  EINVOICE_STATUSES,
  EInvoiceStatus,
} from "../../models/invoice/invoice.model";
import {
  getAllEpcPayments,
  getAllEpcRegisteredUsers,
  getEpcMetaData,
  getSalesRep,
} from "../../store/slices/epcSlice";
import AccountCommissionView from "../CommissionAndRepayment/AccountCommissionView";
import { AllSalesRepInfo } from "../Cse/AllSalesRepInfo";
import PaymentsTable from "../Cse/PaymentsTable";
import RepaymentsTable from "../CommissionAndRepayment/RepaymentsTable";
import { fetchAllOrdersForAccounts } from "../../store/slices/orderSlice";
import {
  getAccountCommissions,
  getAllRepayments,
} from "../../store/slices/commissionAndRepaymentSlice";

const isEInvoiceStatus = (status: string | null): status is EInvoiceStatus =>
  EINVOICE_STATUSES.includes(status as EInvoiceStatus);

const getInvoicePageParam = (value: string | null) => {
  const page = Number(value);
  return Number.isInteger(page) && page > 0 ? page : 1;
};

const PaymentValidation: React.FC = () => {
  // Redux state
  const dispatch: RootDispatch = useDispatch();
  const loading = useSelector((s: RootState) => s.order.loading);
  const { role } = useSelector((s: RootState) => s.auth);

  // Local state
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [paymentType, setPaymentType] = useState<PaymentType>(() => {
    const type = searchParams.get("paymentType");
    return type === "epc" ||
      type === "commission" ||
      type === "rePayment" ||
      type === "SalesRepresentative" ||
      type === "payment" ||
      type === "invoices"
      ? type
      : "order";
  });
  const [search, setSearch] = useState(() =>
    searchParams.get("paymentType") === "invoices"
      ? searchParams.get("invoiceSearch") || ""
      : "",
  );
  const [invoiceStatus, setInvoiceStatus] = useState<EInvoiceStatus>(() => {
    const status = searchParams.get("invoiceStatus");
    return isEInvoiceStatus(status) ? status : "ACTIVE";
  });
  const [invoicePage, setInvoicePage] = useState(() =>
    getInvoicePageParam(searchParams.get("invoicePage")),
  );
  const [invoiceReloadKey, setInvoiceReloadKey] = useState(0);
  const [view, setView] = useState<IOrderValidation>(() => {
    const status = searchParams.get("status");
    return Object.values(IOrderValidation).includes(status as IOrderValidation)
      ? (status as IOrderValidation)
      : IOrderValidation.pending;
  });

  const handlePaymentTypeChange = (type: PaymentType) => {
    const params = new URLSearchParams(searchParams);
    params.set("paymentType", type);
    params.set("status", IOrderValidation.pending);
    if (type === "invoices") {
      const invoiceSearch = params.get("invoiceSearch") || "";
      params.set("invoiceStatus", invoiceStatus);
      params.set("invoicePage", String(invoicePage));
      setSearch(invoiceSearch);
    } else if (paymentType === "invoices") {
      setSearch("");
    }
    setSearchParams(params);
    setPaymentType(type);
    setView(IOrderValidation.pending);
    setInvoicePage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setInvoicePage(1);
    if (paymentType === "invoices") {
      const params = new URLSearchParams(searchParams);
      params.set("invoicePage", "1");
      if (value.trim()) params.set("invoiceSearch", value);
      else params.delete("invoiceSearch");
      setSearchParams(params, { replace: true });
    }
  };

  const handleInvoiceStatusChange = (status: EInvoiceStatus) => {
    setInvoiceStatus(status);
    setInvoicePage(1);
    const params = new URLSearchParams(searchParams);
    params.set("invoiceStatus", status);
    params.set("invoicePage", "1");
    setSearchParams(params);
  };

  const handleInvoicePageChange = (page: number) => {
    setInvoicePage(page);
    const params = new URLSearchParams(searchParams);
    params.set("invoicePage", String(page));
    setSearchParams(params, { replace: true });
  };

  const handleViewChange = (status: IOrderValidation) => {
    const params = new URLSearchParams(searchParams);
    params.set("status", status);
    setSearchParams(params);
    setView(status);
  };

  useEffect(() => {
    if (paymentType !== "invoices") return;
    const params = new URLSearchParams(searchParams);
    const statusParam = searchParams.get("invoiceStatus");
    const status = isEInvoiceStatus(statusParam) ? statusParam : "ACTIVE";
    const pageParam = searchParams.get("invoicePage");
    const page = getInvoicePageParam(pageParam);

    if (statusParam !== status) params.set("invoiceStatus", status);
    if (pageParam !== String(page)) params.set("invoicePage", String(page));

    setInvoiceStatus(status);
    setInvoicePage(page);
    setSearch(searchParams.get("invoiceSearch") || "");
    if (statusParam !== status || pageParam !== String(page)) {
      setSearchParams(params, { replace: true });
    }
  }, [paymentType, searchParams, setSearchParams]);

  // Initialize data
  useEffect(() => {
    dispatch(getEpcMetaData());
    dispatch(getAllEpcRegisteredUsers());

    dispatch(getSalesRep({}));
  }, [dispatch]);

  // Handlers
  const handleLogout = useCallback(() => {
    clearToken();
    navigate("/login");
    window.location.reload();
  }, [navigate]);

  const handleReload = async () => {
    if (paymentType === "order") {
      dispatch(
        fetchAllOrdersForAccounts({
          page: 1,
          search,
          status: search.trim() ? undefined : view,
          payments: true,
        }),
      );
    }
    if (paymentType === "epc") {
      dispatch(
        getAllEpcPayments({
          searchQuery: search,
          page: 1,
          paymentStatus: search.trim() ? undefined : view,
        }),
      );
    }
    if (paymentType === "commission") {
      dispatch(getAccountCommissions({ page: 1, limit: 12, status: view }));
    }
    if (paymentType === "rePayment") {
      await dispatch(
        getAllRepayments({
          page: 1,
          limit: 12,
          search,
          from: "account",
        }),
      );
    }
    if (paymentType === "invoices") {
      setInvoiceReloadKey((key) => key + 1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Navigation Header */}
      <NavigationHeader
        onReload={handleReload}
        paymentType={paymentType}
        setPaymentType={handlePaymentTypeChange}
        view={view}
        setView={handleViewChange}
        role={role!}
        onLogout={handleLogout}
        search={search}
        setSearch={handleSearchChange}
      />

      {/* Main Content */}
      <main className=" mx-auto px-6 py-8">
        {paymentType === "order" || paymentType === "epc" ? (
          <OrderGrid
            paymentType={paymentType}
            search={search}
            view={view}
            loading={loading}
          />
        ) : paymentType === "commission" ? (
          <AccountCommissionView status={view} />
        ) : paymentType === "rePayment" ? (
          <RepaymentsTable repaymentStatus={view} from="account" />
        ) : paymentType === "SalesRepresentative" ? (
          <AllSalesRepInfo />
        ) : paymentType === "payment" ? (
          <PaymentsTable />
        ) : paymentType === "invoices" ? (
          <EinvoicesPage
            search={search}
            status={invoiceStatus}
            onStatusChange={handleInvoiceStatusChange}
            page={invoicePage}
            onPageChange={handleInvoicePageChange}
            reloadKey={invoiceReloadKey}
          />
        ) : null}
      </main>
    </div>
  );
};

export default PaymentValidation;
